// src/components/forms/WiresForm.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { Form, Input, Button, Select, App } from 'antd';
import { WiresItem } from '../../types/ProductItems';
import RefOemManager from '../common/RefOemManager';
import ImageUploader from '../common/ImageUploader';
import { toBase64, mapBase64ToUploadList } from '../../utils/formsThumbnails';
import { fetchProductRefsOems } from '../../services/productAPI';

interface WiresFormProps {
  onAdd: (item: WiresItem) => void;
  initialValues?: WiresItem | null;
}
const { Option } = Select;

const nonNegativeRule = (label: string) => ({
  validator(_: any, value: any) {
    if (value === undefined || value === '' || Number(value) >= 0) return Promise.resolve();
    return Promise.reject(new Error(`${label} не может быть отрицательным`));
  },
});

const WiresForm: React.FC<WiresFormProps> = ({ onAdd, initialValues }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [refList, setRefList] = useState<{ [brand: string]: string[] }>({});
  const [oemList, setOemList] = useState<{ [model: string]: string[] }>({});

  const resetForm = useCallback(() => {
    form.resetFields();
    setRefList({});
    setOemList({});
  }, [form]);

  useEffect(() => {
    let mounted = true;
    if (initialValues) {
      const { images = [], ...rest } = initialValues;
      form.setFieldsValue({
        ...rest,
        images: mapBase64ToUploadList(images),
      });
      setRefList(initialValues.references ?? {});
      setOemList(initialValues.oems ?? {});
      const mysqlId = Number(initialValues.id);
      if (Number.isInteger(mysqlId) && mysqlId > 0) {
        fetchProductRefsOems('ignition_coil_mouthpieces', mysqlId).then(fresh => {
          if (fresh && mounted) {
            setRefList(fresh.references);
            setOemList(fresh.oems);
          }
        });
      }
    } else {
      resetForm();
    }
    return () => { mounted = false; };
  }, [initialValues, form, resetForm]);

  const handleFinish = async (values: any) => {
    try {
      const imagesField = values.images || [];
      const base64Images = await Promise.all(
        imagesField.map(async (file: any) => {
          if (file?.originFileObj) return await toBase64(file.originFileObj);
          return file?.url || file?.thumbUrl;
        })
      );

      const rawLen = values.length;
      const parsedLength =
        rawLen != null && rawLen !== ''
          ? parseFloat(String(rawLen).replace(',', '.')) || undefined
          : undefined;

      const originalServerUrls = (initialValues?.images ?? [])
        .filter((img: string) => typeof img === 'string' && !img.startsWith('data:'));
      const _deletedImgs = originalServerUrls.filter((img: string) => !base64Images.includes(img));

      const newItem: WiresItem = {
        id: initialValues?.id || crypto.randomUUID(),
        key: initialValues?.key || Date.now().toString(),
        ...values,
        length: parsedLength,
        images: base64Images,
        references: refList,
        oems: oemList,
        _deletedImgs,
      } as any;

      await onAdd(newItem);
      message.success('Товар добавлен или обновлён!');
      resetForm();
    } catch {
      message.error('Заполните все обязательные поля');
    }
  };

  return (
    <Form
      form={form}
      name="wiresForm"
      layout="vertical"
      preserve={false}
      onFinish={handleFinish}
    >
      <Form.Item label="Бренд" rules={[{ required: true }]} name="brand">
        <Input />
      </Form.Item>

      <Form.Item label="Номер детали" rules={[{ required: true }]} name="partNumber">
        <Input />
      </Form.Item>

      <Form.Item label="SKU" name="sku">
        <Input maxLength={10} />
      </Form.Item>

      <Form.Item label="Модель" rules={[{ required: true }]} name="model">
        <Input />
      </Form.Item>

      <Form.Item label="Цена" rules={[{ required: true }, nonNegativeRule('Цена')]} name="price">
        <Input type="number" />
      </Form.Item>

      <Form.Item label="Количество (в наличии)" rules={[{ required: true }, nonNegativeRule('Количество')]} name="quantity">
        <Input type="number" />
      </Form.Item>

      <Form.Item
        label="Длина (мм)"
        name="length"
        rules={[{
          validator: (_, value) => {
            if (value == null || value === '') return Promise.resolve();
            const n = parseFloat(String(value).replace(',', '.'));
            if (isNaN(n) || n < 0) return Promise.reject(new Error('Введите число: 130.7 или 130,7'));
            return Promise.resolve();
          },
        }]}
      >
        <Input />
      </Form.Item>

      <Form.Item label="Тип наконечника" rules={[{ required: true }]} name="tipType">
        <Select placeholder="Выберите тип">
          <Option value="провод">Провод</Option>
          <Option value="наконечник">Наконечник</Option>
        </Select>
      </Form.Item>

      <Form.Item name="original" label="Тип товара" rules={[{ required: true }]}>
        <Select placeholder="Выберите тип">
          <Option value="original">Оригинал</Option>
          <Option value="copy">Копия</Option>
        </Select>
      </Form.Item>

      <RefOemManager title="REF" data={refList} setData={setRefList} />
      <RefOemManager title="OEM" data={oemList} setData={setOemList} />
      <ImageUploader />

      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={resetForm}>Очистить форму</Button>
        <Button type="primary" htmlType="submit">
          Добавить Продукт
        </Button>
      </div>
    </Form>
  );
};

export default WiresForm;
